import { findEntity } from '@app/icare/entities/registry'
import { t } from '@lingui/core/macro'
import type { AnyEntityId, EntityIdOf } from '@pkg/db/schema'
import { getTableById } from '@pkg/db/utils'
import { zodDefaultInstance } from '@pkg/shared/form'
import { logger } from '@pkg/shared/logger'
import { toast } from '@pkg/ui/toast'
import type { FormAsyncValidateOrFn, FormOptions, FormValidateOrFn, StandardSchemaV1 } from '@tanstack/form-core'
import { formOptions } from '@tanstack/react-form'
import type { ReactNode } from 'react'
import type { z } from 'zod'

export type FormToastConfig = {
  successTitle?: ReactNode
  errorTitle?: ReactNode
  description?: string
}

const successTitle = (c: FormToastConfig, result?: { id?: AnyEntityId }) => {
  // biome-ignore lint/nursery/noMisusedPromises: Not a promise
  if (c?.successTitle) {
    return c.successTitle as ReactNode
  }
  if (result?.id) {
    const entity = findEntity(getTableById(result.id))
    const label = entity?.label
    if (label) {
      return t`${label} gespeichert`
    }
  }
  return t`Gespeichert`
}
const errorTitle = (c?: FormToastConfig) => c?.errorTitle ?? t`Speichern fehlgeschlagen`

// Overload 1: with schema — locks TFormData to schema output, wires validators, and adds toast
export function appFormOptions<
  T extends z.ZodType,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnBlur extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnSubmit extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnDynamic extends undefined | FormValidateOrFn<z.output<T>> = undefined,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TOnServer extends undefined | FormAsyncValidateOrFn<z.output<T>> = undefined,
  TSubmitMeta = never,
>(
  schema: T,
  opts: Partial<
    FormOptions<
      z.output<T>,
      StandardSchemaV1<z.output<T>>,
      StandardSchemaV1<z.output<T>>,
      TOnChangeAsync,
      TOnBlur,
      TOnBlurAsync,
      TOnSubmit,
      TOnSubmitAsync,
      TOnDynamic,
      TOnDynamicAsync,
      TOnServer,
      TSubmitMeta
    >
  > & { toast?: FormToastConfig | false; id?: EntityIdOf<z.output<T>> },
): FormOptions<
  z.output<T>,
  StandardSchemaV1<z.output<T>>,
  StandardSchemaV1<z.output<T>>,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnDynamic,
  TOnDynamicAsync,
  TOnServer,
  TSubmitMeta
>

// Overload 2: without schema — generic list mirrors formOptions/FormOptions
export function appFormOptions<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnChange extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnBlur extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnDynamic extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TSubmitMeta = never,
>(
  opts: FormOptions<
    TFormData,
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  > & { toast?: FormToastConfig | false; id?: EntityIdOf<TFormData> },
): FormOptions<
  TFormData,
  TOnMount,
  TOnChange,
  TOnChangeAsync,
  TOnBlur,
  TOnBlurAsync,
  TOnSubmit,
  TOnSubmitAsync,
  TOnDynamic,
  TOnDynamicAsync,
  TOnServer,
  TSubmitMeta
>

export function appFormOptions<
  TFormData,
  TOnMount extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnChange extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnBlur extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnSubmit extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnDynamic extends undefined | FormValidateOrFn<TFormData> = undefined,
  TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TOnServer extends undefined | FormAsyncValidateOrFn<TFormData> = undefined,
  TSubmitMeta = never,
>(schemaOrOpts: unknown, opts?: unknown): unknown {
  // Check if first arg is a schema (ZodType) or form options by checking for '_def' property
  const isSchema =
    schemaOrOpts != null && typeof schemaOrOpts === 'object' && '_def' in schemaOrOpts && opts !== undefined

  type CurrentFormOptions = FormOptions<
    TFormData,
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >

  const options: CurrentFormOptions & { toast?: FormToastConfig | false; id?: EntityIdOf<TFormData> } = (
    isSchema ? opts : schemaOrOpts
  ) as never
  // biome-ignore lint/suspicious/noExplicitAny: ignored here
  const Schema = isSchema ? (schemaOrOpts as z.ZodObject<any>) : undefined
  const validators = isSchema
    ? {
        // onMount: Schema,
        onChange: Schema,
        ...options?.validators,
      }
    : options.validators

  const defaultValues = options?.defaultValues ?? (Schema ? (zodDefaultInstance(Schema) as TFormData) : undefined)

  const { toast: toastCfg, id, onSubmit, onSubmitInvalid, ...rest } = options || {}

  const wrappedOnSubmitInvalid: CurrentFormOptions['onSubmitInvalid'] = async params => {
    const errors = params.formApi.getAllErrors()
    if (errors) {
      logger.warn({ errors }, `Trying to submit invalid form`)
    }
    return onSubmitInvalid?.(params)
  }

  const wrappedOnSubmit: CurrentFormOptions['onSubmit'] = async params => {
    try {
      const result = await onSubmit?.(params)
      if (toastCfg !== false) {
        const resolved = id ? { id } : (result ?? (params as { value?: { id?: AnyEntityId } }).value)
        toast.success({
          title: successTitle(toastCfg ?? {}, resolved),
          description: toastCfg?.description,
        })
      }
      return result
    } catch (err) {
      if (toastCfg !== false) {
        toast.error({ title: errorTitle(toastCfg), description: toastCfg?.description })
      }
      throw err
    }
  }

  return formOptions({
    ...rest,
    defaultValues,
    validators: validators as never,
    onSubmit: wrappedOnSubmit,
    onSubmitInvalid: wrappedOnSubmitInvalid,
  })
}
